import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import ServiceDialog, { CATEGORIES } from "./ServiceDialog";

function ServiceRow({ svc, onEdit, onDelete }) {
  return (
    <div data-testid={`menu-row-${svc.id}`} className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/5 last:border-0">
      <div className="min-w-0">
        <div className="text-white flex items-center gap-2">
          {svc.is_special && <Star className="w-3.5 h-3.5 text-[#FF2079] shrink-0" />}
          <span className="truncate">{svc.name}</span>
        </div>
        <div className="text-xs text-[#A09CA3]">{svc.duration_min} min</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span data-testid={`menu-price-${svc.id}`} className="font-display text-xl text-[#FF2079]">{svc.price_label || `$${svc.price}`}</span>
        <button data-testid={`menu-edit-${svc.id}-btn`} onClick={() => onEdit(svc)} className="p-2 text-[#A09CA3] hover:text-white" aria-label="Edit">
          <Pencil className="w-4 h-4" />
        </button>
        <button data-testid={`menu-delete-${svc.id}-btn`} onClick={() => onDelete(svc)} className="p-2 text-[#A09CA3] hover:text-red-400" aria-label="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function MenuManager() {
  const [services, setServices] = useState([]);
  const [dialog, setDialog] = useState({ open: false, service: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/services");
    setServices(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      const { id, order, ...body } = form;
      if (dialog.service) await api.put(`/services/${dialog.service.id}`, body);
      else await api.post("/services", body);
      toast.success("Menu updated.");
      setDialog({ open: false, service: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Remove "${svc.name}" from the menu?`)) return;
    try {
      await api.delete(`/services/${svc.id}`);
      toast.success("Service removed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="menu-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="eyebrow">Services & Prices</div>
        <Button data-testid="add-service-btn" onClick={() => setDialog({ open: true, service: null })} className="pink-glow-btn bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-10 px-5 text-[10px] tracking-[0.28em] uppercase">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Service
        </Button>
      </div>
      <div className="space-y-8">
        {CATEGORIES.map((cat) => {
          const items = services.filter((s) => s.category === cat);
          if (!items.length) return null;
          return (
            <div key={cat} data-testid={`menu-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-2xl text-white mb-3">{cat}</div>
              <div className="glass-card rounded-sm">
                {items.map((svc) => (
                  <ServiceRow key={svc.id} svc={svc} onEdit={(s) => setDialog({ open: true, service: s })} onDelete={remove} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <ServiceDialog open={dialog.open} service={dialog.service} saving={saving} onSave={save} onClose={() => setDialog({ open: false, service: null })} />
    </div>
  );
}
